import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import { 
  Save, 
  Notifications, 
  Email,
  Sms,
  NotificationsActive,
  Inventory2,
  Event,
  Payment,
  ShoppingCart,
  CheckCircle
} from '@mui/icons-material';
import { useLanguage } from '../../../contexts/LanguageContext';
import config from '../../../config';
import axios from 'axios';

const translations = {
  ar: {
    title: 'إعدادات الإشعارات',
    subtitle: 'تخصيص التنبيهات والإشعارات حسب تفضيلاتك',
    channels: 'قنوات التوصيل',
    alertTypes: 'أنواع التنبيهات',
    emailNotifications: 'إشعارات البريد الإلكتروني',
    smsNotifications: 'إشعارات الرسائل النصية',
    systemNotifications: 'إشعارات النظام',
    lowStockAlert: 'تنبيه المخزون المنخفض',
    expiryAlert: 'تنبيه تاريخ الانتهاء',
    paymentDue: 'تنبيه استحقاق الدفع',
    newOrder: 'طلب جديد',
    approvalRequired: 'تتطلب موافقة',
    saveChanges: 'حفظ التغييرات',
    saveSuccess: 'تم حفظ الإعدادات بنجاح',
    saveError: 'فشل حفظ الإعدادات',
    active: 'نشط',
    inactive: 'غير نشط'
  },
  en: {
    title: 'Notification Settings',
    subtitle: 'Customize alerts and notifications according to your preferences',
    channels: 'Delivery Channels',
    alertTypes: 'Alert Types',
    emailNotifications: 'Email Notifications',
    smsNotifications: 'SMS Notifications',
    systemNotifications: 'System Notifications',
    lowStockAlert: 'Low Stock Alert',
    expiryAlert: 'Expiry Alert',
    paymentDue: 'Payment Due Alert',
    newOrder: 'New Order',
    approvalRequired: 'Approval Required',
    saveChanges: 'Save Changes',
    saveSuccess: 'Settings saved successfully',
    saveError: 'Failed to save settings',
    active: 'Active',
    inactive: 'Inactive'
  }
};

export default function NotificationsTab() {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations];
  const isRTL = language === 'ar';
  
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    systemNotifications: true,
    lowStockAlert: true,
    expiryAlert: true,
    paymentDue: true,
    newOrder: true,
    approvalRequired: true
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      
      const token = localStorage.getItem('token');
      await axios.post(`${config.API_BASE_URL}/SystemSettings/bulk`, 
        Object.entries(settings).map(([key, value]) => ({
          settingKey: key,
          settingValue: value.toString(),
          category: 'Notifications'
        })),
        { headers: { Authorization: `Bearer ${token}` }}
      );

      setMessage({ type: 'success', text: t.saveSuccess });
    } catch (error: any) {
      setMessage({ type: 'error', text: t.saveError });
    } finally {
      setSaving(false);
    }
  };

  const getActiveCount = () => {
    return Object.values(settings).filter(Boolean).length;
  };

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)',
          color: 'white',
          p: 4,
          borderRadius: 3,
          mb: 3,
          boxShadow: '0 8px 32px rgba(255, 107, 107, 0.3)'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <NotificationsActive sx={{ fontSize: 40 }} />
          <Box flex={1}>
            <Typography variant="h4" fontWeight="bold">
              {t.title}
            </Typography>
            <Typography variant="body2" component="div" sx={{ opacity: 0.9, mt: 0.5 }}>
              {t.subtitle}
            </Typography>
          </Box>
          <Chip
            label={`${getActiveCount()}/8 ${t.active}`}
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.2)', 
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.9rem'
            }}
          />
        </Stack>
      </Box>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3, borderRadius: 2 }} 
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Channels Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(255, 107, 107, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <Notifications sx={{ color: '#ff6b6b', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.channels}
              </Typography>
            </Stack>

            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={handleChange}
                    name="emailNotifications"
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Email sx={{ fontSize: 20, color: settings.emailNotifications ? '#ff6b6b' : '#bbb' }} />
                    <span>{t.emailNotifications}</span>
                  </Stack>
                }
              />

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.smsNotifications}
                    onChange={handleChange}
                    name="smsNotifications"
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Sms sx={{ fontSize: 20, color: settings.smsNotifications ? '#ff6b6b' : '#bbb' }} />
                    <span>{t.smsNotifications}</span>
                  </Stack>
                }
              />

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.systemNotifications}
                    onChange={handleChange}
                    name="systemNotifications"
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <NotificationsActive sx={{ fontSize: 20, color: settings.systemNotifications ? '#ff6b6b' : '#bbb' }} />
                    <span>{t.systemNotifications}</span>
                  </Stack>
                }
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Alert Types Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(254, 202, 87, 0.3)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <NotificationsActive sx={{ color: '#feca57', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.alertTypes}
              </Typography>
            </Stack>

            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.lowStockAlert}
                    onChange={handleChange}
                    name="lowStockAlert"
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Inventory2 sx={{ fontSize: 20, color: settings.lowStockAlert ? '#ff6b6b' : '#bbb' }} />
                    <span>{t.lowStockAlert}</span>
                  </Stack>
                }
              />

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.expiryAlert}
                    onChange={handleChange}
                    name="expiryAlert"
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Event sx={{ fontSize: 20, color: settings.expiryAlert ? '#ff6b6b' : '#bbb' }} />
                    <span>{t.expiryAlert}</span>
                  </Stack>
                }
              />

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.paymentDue}
                    onChange={handleChange}
                    name="paymentDue"
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Payment sx={{ fontSize: 20, color: settings.paymentDue ? '#ff6b6b' : '#bbb' }} />
                    <span>{t.paymentDue}</span>
                  </Stack>
                }
              />

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.newOrder}
                    onChange={handleChange}
                    name="newOrder"
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ShoppingCart sx={{ fontSize: 20, color: settings.newOrder ? '#ff6b6b' : '#bbb' }} />
                    <span>{t.newOrder}</span>
                  </Stack>
                }
              />

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.approvalRequired}
                    onChange={handleChange}
                    name="approvalRequired"
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircle sx={{ fontSize: 20, color: settings.approvalRequired ? '#ff6b6b' : '#bbb' }} />
                    <span>{t.approvalRequired}</span>
                  </Stack>
                }
              />
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            px: 5,
            py: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)',
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(255, 107, 107, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #ee5a52 0%, #f5bb3f 100%)',
              boxShadow: '0 6px 20px rgba(255, 107, 107, 0.6)',
            }
          }}
        >
          {t.saveChanges}
        </Button>
      </Box>
    </Box>
  );
}